import { throttle } from 'lodash';
import { accSub } from '@/utils/math';

interface Position {
  scrollTop: number;
  scrollLeft?: number;
  timestamp?: number;
}

const SCROLL_POSITIONS = 'SCROLL-POSITIONS';

const maxAge = 1000 * 60 * 60 * 24; // 24h
// 滚动容器
let scrollContainer: HTMLElement | null;
let scrollPositions: Recordable = {};
let currentPath: string = '';

// 滚动事件回调
const scrollHandler = throttle(() => {
  if (!scrollContainer || !currentPath) return;
  // 标记发生了滚动
  saveScrollPosition(currentPath, {
    scrollLeft: scrollContainer.scrollLeft || 0,
    scrollTop: scrollContainer.scrollTop || 0,
  });
}, 500);

export function initScrollPositions(el: string | HTMLElement) {
  try {
    scrollPositions = JSON.parse(
      sessionStorage.getItem(SCROLL_POSITIONS) || '{}'
    );
    scrollContainer = typeof el === 'string' ? document.querySelector(el) : el;
    bindScrollListener();
  } catch {
    scrollPositions = {};
  }
}

function bindScrollListener() {
  if (!scrollContainer) return;
  scrollContainer.addEventListener('scroll', scrollHandler, { passive: true });
}
export function removeScrollListener() {
  if (!scrollContainer) return;
  scrollContainer.removeEventListener('scroll', scrollHandler);
  cleanupScrollPositionStore();
}

// 滚动到指定位置
export function scrollToPosition(position: Position) {
  if (!scrollContainer) return;

  const targetScrollTop = position.scrollTop || 0;
  let hasScrolled = false;
  let initialCheckDone = false;
  let lastSetScrollTop = -1; // 记录上次设置的滚动位置

  const trySetScroll = () => {
    if (!scrollContainer || hasScrolled) return true;

    const maxScrollTop =
      accSub(
        scrollContainer.scrollHeight || 0,
        scrollContainer.clientHeight || 0
      ) || 0;

    // 如果可以滚动到目标位置，设置滚动位置
    if (
      maxScrollTop >= targetScrollTop &&
      lastSetScrollTop !== targetScrollTop
    ) {
      // scrollContainer.scrollTop = targetScrollTop;
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = targetScrollTop;
        }
      });
      lastSetScrollTop = targetScrollTop;
      hasScrolled = true;
      return true;
    }

    // 如果不能滚动到目标位置，但可以滚动到最大位置，则滚动到最大位置
    if (maxScrollTop < targetScrollTop && lastSetScrollTop !== maxScrollTop) {
      // scrollContainer.scrollTop = targetScrollTop;
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = targetScrollTop;
        }
      });
      lastSetScrollTop = maxScrollTop;
      // 这种情况下不设置 hasScrolled 为 true，继续监听可能的高度变化
      return false; // 继续监听，可能后续内容加载后可以滚动到目标位置
    }

    return false;
  };

  // 立即尝试一次
  trySetScroll();

  // 使用 ResizeObserver 统一监听所有变化
  const resizeObserver = new ResizeObserver(() => {
    // 第一次检查后设置标记
    if (!initialCheckDone) {
      initialCheckDone = true;
      // 如果第一次就能滚动到目标位置，短暂监听以防高度变化
      if (hasScrolled) {
        setTimeout(() => {
          resizeObserver.disconnect();
        }, 1500); // 1.5秒后停止监听
        return;
      }
    }

    // 尝试设置滚动位置
    if (trySetScroll()) {
      resizeObserver.disconnect();
    }
  });

  resizeObserver.observe(scrollContainer);

  // 设置超时，避免无限监听
  setTimeout(() => {
    resizeObserver.disconnect();
    // 超时后最后一次尝试设置滚动位置，确保滚动到当前可达到的最大位置
    trySetScroll();
  }, 2000);
}

export function setCurrentPath(path: string) {
  currentPath = path;
}

// 获取滚动位置存储
export function getScrollPositionStore() {
  return scrollPositions;
}
// 保存滚动位置存储
export function saveScrollPositionStore() {
  sessionStorage.setItem(SCROLL_POSITIONS, JSON.stringify(scrollPositions));
}

// 获取滚动位置
export function getScrollPosition(path: string) {
  return scrollPositions[path];
}
// 保存滚动位置
export function saveScrollPosition(path: string, position: Recordable = {}) {
  // 只有在发生过滚动时才保存
  if (!path) return;
  scrollPositions[path] = {
    ...position,
    timestamp: Date.now(),
  };
  sessionStorage.setItem(SCROLL_POSITIONS, JSON.stringify(scrollPositions));
}

// 清除过期的滚动位置
export function clearScrollPosition() {
  let hasDelete = false;
  Object.keys(scrollPositions).forEach((key: string) => {
    const position = scrollPositions[key];
    if (Date.now() - position.timestamp > maxAge) {
      delete scrollPositions[key];
      hasDelete = true;
    }
  });
  if (hasDelete) {
    // 有改动才保存
    saveScrollPositionStore();
  }
}

export function cleanupScrollPositionStore() {
  sessionStorage.removeItem(SCROLL_POSITIONS);
}
